import { Box as MuiBox, Typography as MuiTypography } from "@mui/material";
import { DataGridPro as MuiDataGridPro } from "@mui/x-data-grid-pro";
import { useState, useEffect, memo } from "react";
import { useTranslation } from "react-i18next";
import isJSON from "is-json";
import {
  setFieldsInText,
  replaceFieldCalculationValues,
  shouldRenderElement,
  addFieldValidation,
} from "app/shared/utils";

const MuiDataGridStyles = {
  "& .MuiDataGrid-filler": {
    display: "none",
  },
  "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within, &.MuiDataGrid-root .MuiDataGrid-cell:focus-within":
    {
      outline: "none",
    },
};

export const Table = memo((props) => {
  const { t } = useTranslation();
  let { label, formValues } = props;
  const {
    name,
    tableColumnDefinition,
    actionid,
    formspec,
    element,
    formFlatMap,
    globalValueObj,
    formik,
    fieldAttr,
    helpertext,
    validationType,
    validationScheme,
    validationMessage,
    yup,
    duplicateIds,
  } = props;

  const [, setText] = useState();
  const [exp, setExp] = useState("");
  const [shouldRender, setShouldRender] = useState(false);
  const [fieldRenderedExp, setFieldRenderedExp] = useState(true);
  const [rows, setRows] = useState([]);
  const [, setFieldHelper] = useState(helpertext);
  const [, setFieldValidation] = useState(false);

  const { defaultValue = "", rowsPerPage = 3 } = isJSON(fieldAttr)
    ? JSON.parse(fieldAttr)
    : {};

  const getTableColumnDefinitionMap = (options) =>
    Array.isArray(options)
      ? options.map((item) => {
          return {
            field: item.id,
            headerName: item.text,
            sortable: false,
            flex: 1,
          };
        })
      : [];

  // Build a stable row id using an existing row id when available, else use the row index.
  const createDataGridRowId = (row, index) => {
    const rowId = row?.id ?? row?.rowId ?? row?.row_id;
    const hasRowId =
      rowId !== undefined && rowId !== null && String(rowId).trim() !== "";

    return hasRowId
      ? `DataGridRow${String(rowId)}-${index}`
      : `DataGridRow${index}`;
  };

  // Normalize each row and attach the generated id for DataGrid getRowId.
  const rowsWithIds = Array.isArray(rows)
    ? rows.map((row, index) => {
        const safeRow = row && typeof row === "object" ? row : { value: row };

        return {
          ...safeRow,
          dataGridRowId: createDataGridRowId(safeRow, index),
        };
      })
    : [];

  useEffect(() => {
    // Field Rendered evaluation - Should render this component or not, will depend on field rendered evaluation
    setFieldRenderedExp(
      shouldRenderElement(
        element.field_rendered,
        element.field_value_type,
        {
          ...formFlatMap,
          ...globalValueObj,
          ...formik.values,
        },
        element.field_editable,
      ),
    );
  }, [
    element.field_id,
    element.field_rendered,
    element.field_editable,
    globalValueObj,
    formFlatMap,
    formik,
    element.field_value_type,
  ]);

  useEffect(() => {
    try {
      // eslint-disable-next-line no-eval
      const evaledExp = eval(fieldRenderedExp); // SYNC and ASYNC

      addFieldValidation({
        name,
        evaledExp,
        required: false,
        touched: false,
        helpertext,
        validationType,
        validationScheme,
        validationMessage,
        t,
        formik,
        setFieldValidation,
        setFieldHelper,
        formFlatMap: formValues,
        globalValueObj,
        yup,
        element,
        duplicateIds,
      });

      setShouldRender(evaledExp);
    } catch (err) {
      console.log(name, err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldRenderedExp, name]);

  useEffect(() => {
    if (formik && element) {
      const calculatedExp = getCalculatedExp(
        element.field_calculation,
        {
          ...formFlatMap,
          ...globalValueObj,
          ...formik.values,
        },
        element.field_value_type,
        element.field_editable,
      );
      setExp(calculatedExp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik, globalValueObj]);

  const getCalculatedExp = (
    fieldCalculation,
    formValueObj,
    valueType,
    fieldEditable,
  ) => {
    return replaceFieldCalculationValues(
      fieldCalculation,
      formValueObj,
      valueType,
      fieldEditable,
    );
  };

  useEffect(() => {
    try {
      // eslint-disable-next-line no-eval
      const evaledExp = eval(exp) ?? [];
      const formattedEvaledExp =
        evaledExp && Array.isArray(evaledExp)
          ? evaledExp
          : evaledExp
            ? JSON.parse(evaledExp)
            : [];

      const fieldValue = shouldRender ? formattedEvaledExp : [];

      if (exp || String(fieldValue)) {
        globalValueObj[name] = fieldValue;
        formik.values[name] = fieldValue;
        formFlatMap[name] = fieldValue;
        setRows(fieldValue);
      }
    } catch (err) {
      console.log(name, err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exp, name, shouldRender, defaultValue]);

  useEffect(() => {
    setFieldsInText(
      label,
      formValues,
      {
        actionId: actionid,
        formSpec: formspec,
      },
      setText,
    );
  }, [label, formValues, actionid, formspec]);

  if (!tableColumnDefinition?.length) return null;

  if (!shouldRender) return null;

  return (
    <MuiBox
      id={`field-${element.field_id}`}
      sx={{
        width: "100%",
      }}
    >
      <MuiBox>
        <MuiTypography
          variant="body2"
          sx={{
            pb: 1,
          }}
        >
          {element.field_label}
        </MuiTypography>
      </MuiBox>
      <MuiBox>
        <MuiDataGridPro
          rows={rowsWithIds}
          density="compact"
          hideFooter
          disableColumnMenu
          disableColumnResize
          disableColumnReorder
          sx={MuiDataGridStyles}
          disableRowSelectionOnClick
          getRowId={(row) => row.dataGridRowId}
          columns={getTableColumnDefinitionMap(tableColumnDefinition)}
          pagination
          initialState={{
            pagination: { paginationModel: { pageSize: rowsPerPage } },
          }}
        />
      </MuiBox>
    </MuiBox>
  );
});
